import {
  action, computed, makeObservable, observable,
} from 'mobx';
import { nanoid } from 'nanoid';
import { DisplayMode } from '@api-consent/types/common.js';
import { ConsentStore } from '@src/state/ConsentStore.js';
import { ConsentGroup } from '@src/types/comsApi.js';

export class ConsentGroupStore implements ConsentGroup {
  private $id: string;

  consentPrefix?: string;
  consents: ConsentStore[] = [];
  denyall?: string;
  denyallView?: string;
  displayMode?: DisplayMode;
  footer?: string;
  grantedDefault?: boolean;
  header?: string;
  name: string = '';
  oneclick?: boolean;
  oneclickCollapsible?: string;
  oneclickScope?: string;
  required?: boolean;
  text?: string;

  collapsibleOpen = false;

  constructor(data: ConsentGroup, fromStorage: boolean) {
    makeObservable(this, {
      consents: observable,
      areAllConsentsDenied: computed,
      areAllConsentsAndFeaturesGranted: computed,
      collapsibleOpen: observable,
      oneClickToggle: action,
      denyAll: action,
    });
    const { consents, ...restData } = data;
    Object.assign(this, restData);

    this.consents = consents.map((c) => {
      const consentValue = { ...c };

      if (!fromStorage) {
        if (this.required) {
          consentValue.initialGranted = true;
          consentValue.granted = true;
        } else if (typeof consentValue.granted !== 'undefined') {
          consentValue.initialGranted = consentValue.granted;
          consentValue.originalGranted = consentValue.granted;
        }
      }

      return new ConsentStore(consentValue);
    });
    this.$id = nanoid();
  }

  oneClickToggle(e?: PointerEvent): void {
    e?.preventDefault(); // prevent double triggering
    const currentState = this.areAllConsentsAndFeaturesGranted;
    this.consents.forEach((consent) => {
      consent.setGrantedWithFeatures(!currentState);
    });
  }

  denyAll(e?: PointerEvent): void {
    e?.preventDefault(); // prevent double triggering
    if (!this.areAllConsentsDenied) {
      this.consents.forEach((consent) => {
        consent.setGrantedWithFeatures(false);
      });
    } else {
      this.consents.forEach((c) => c.resetToInitial());
    }
  }

  get areAllConsentsDenied(): boolean {
    return !this.consents.find((c) => c.granted);
  }

  get areAllConsentsAndFeaturesGranted(): boolean {
    return !this.consents.find((c) => !c.granted || c.hasNotGrantedFeature);
  }

  get id(): string {
    return this.$id;
  }

  public toJSON(): ConsentGroup {
    return {
      ...this,
      consents: this.consents.map((cs) => cs.toJSON()),
    };
  }
}
